import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import RanDataFormInputs from '@travel/traveler-core/components/RanDataFormInputs';
import { DESKTOP_SCREEN, useDeviceType, useTranslation } from '@travel/traveler-core/hooks';
import { useDatesChange } from '@travel/traveler-core/hooks/useDatesChange';
import { getRanData } from '@travel/traveler-core/store/ranStore/selectors';
import { FlatButton } from '@travel/ui';
import { cx } from '@travel/utils';

import AmountInput from 'components/AmountInput';
import DateInput from 'components/DateInput';
import ErrorMessage from 'components/ErrorMessage';
import Form, { FormSubmit } from 'components/Form';
import PlaceInput from 'components/PlaceInput';
import SalesPromotionInput, { SalesPromotionFormQuery } from 'components/SalesPromotionInput';
import { RoomFormQuery } from 'pages/ProviderListPage/components/SearchForm';

import { autoFilledCheckInDate, autoFilledCheckOutDate } from 'constants/date';
import { EMPTY_ARRAY } from 'constants/defaultValue';
import { Translate } from 'core/translate';
import usePlaceRedirect from 'hooks/usePlaceRedirect';
import { SuggestionsItem } from 'Suggestions-Types';

import styles from './searchForm.module.scss';

const DATE_FORMAT = 'YYYYMMDD';
type Props = {
  /** Flag to define whether search is error or not */
  isSearchError?: boolean;
  /** Callback to be called when search with empty text */
  setIsSearchError?: (isError: boolean) => void;
  /** String of current area path id */
  pathId: string;
  /** String of current area name */
  areaName: string;
  /** Object of current searched query */
  query?: SalesPromotionFormQuery & RoomFormQuery;
  /** Title */
  title: React.ReactNode;
};

function SearchForm(props: Props) {
  const { isSearchError, setIsSearchError, pathId, query, title } = props;
  const parentPlaceId = useRef(pathId);
  const [place, setPlace] = useState({} as SuggestionsItem);
  const { isDatesSet, onDatesSet } = useDatesChange(true);
  const [isAutoOpenDatePicker, setIsAutoOpenDatePicker] = useState(false);
  const isPC = useDeviceType() === DESKTOP_SCREEN;

  const destinationTextLabel = useTranslation({
    id: 'AreaTop.Search',
    data: { area_name: props.areaName },
  });

  const ranData = useSelector(getRanData);

  const placeRedirect = usePlaceRedirect();

  const onSubmit = ({ query }: FormSubmit) => {
    if (!place.pathId) {
      setIsSearchError?.(true);
      return false;
    }
    placeRedirect(place, query);
  };

  const onChangePlace = (place: SuggestionsItem) => {
    setIsSearchError?.(!Boolean(place.pathId));
    setPlace(place);
  };

  const onSelectPlace = () => {
    !isDatesSet && setIsAutoOpenDatePicker(true);
  };

  const errorMessage = (
    <Translate
      id="Traveler_Input_Error.Global_Search.Destination_Search.No_Input"
      className={styles.errorMessage}
    />
  );

  return (
    <Form data-testid="searchForm-form-wrapper" className={styles.wrapper} onSubmit={onSubmit}>
      <h1 className={styles.titleWrapper}>{title}</h1>

      <div className={styles.fields}>
        <div className={styles.placeInputWrapper}>
          <PlaceInput
            onPage="area"
            onChangePlace={onChangePlace}
            onSelectPlace={onSelectPlace}
            placeId={place.pathId}
            destinationTextLabel={destinationTextLabel}
            className={cx(styles.placeInput, isSearchError && styles.hasError)}
            pathIdScope={parentPlaceId.current}
            hasError={isSearchError}
            errorMessage={errorMessage}
            errorMessageClassName={styles.errorMessageWrapper}
            hasAnimation={true}
          />
        </div>
        <DateInput
          onPage="area"
          className={styles.dateInput}
          startDate={query?.startDate || autoFilledCheckInDate.format(DATE_FORMAT)}
          endDate={query?.endDate || autoFilledCheckOutDate.format(DATE_FORMAT)}
          onDateChanged={onDatesSet}
          isAutoOpenDatePicker={isAutoOpenDatePicker}
          setIsAutoOpenDatePicker={setIsAutoOpenDatePicker}
          hasAnimationDialog={true}
        />
        <AmountInput
          onPage="area"
          className={styles.amountInput}
          popupWrapperClassName={styles.amountInputPopup}
          defaultAdults={Number(query?.adults)}
          defaultRooms={Number(query?.noOfUnits)}
          defaultChildrenAges={
            query?.childrenAges ? query.childrenAges.split(',').map(Number) : EMPTY_ARRAY
          }
          isDatesSet={isDatesSet}
          hasAnimationDialog={true}
        />

        <SalesPromotionInput
          salesPromotionId={query?.salesPromotionId}
          promotionCode={query?.promotionCode}
        />

        <RanDataFormInputs ranData={ranData} />

        <FlatButton
          type="submit"
          className={styles.searchButton}
          data-testid="searchForm-search-button"
        >
          <Translate id="Top.Search_Button" />
        </FlatButton>
      </div>
      {isPC && isSearchError && (
        <ErrorMessage iconSize={16} message={errorMessage} className={styles.errorMessageWrapper} />
      )}
    </Form>
  );
}

export default SearchForm;
